/**
 * Your own account.
 *
 * One screen for every role, reached from the sidebar, and the only thing on it
 * is the password. There is nothing else a person may change about their own
 * account — the display name, the role and the volunteer it speaks for are all
 * staff decisions, and a profile page that shows them as read-only fields
 * invites the question of why they cannot be edited.
 *
 * It doubles as the forced-change screen. When an admin has reset the password,
 * `ForcedPasswordChange` renders this same form with different words around it
 * rather than a second copy that would drift.
 */

import { useState } from 'react';
import { isPasswordValid, passwordProblems } from '@parinaam-vms/shared';
import {
  Button,
  Card,
  PasswordInput,
  PasswordExpiry,
  PasswordRules,
} from '../../components/common';
import { useAuth } from '../../hooks/useAuth';
import { ApiRequestError } from '../../services/api/client';
import styles from './ProfilePage.module.css';

export function ProfilePage() {
  const { user } = useAuth();
  if (!user) return null;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>Your account</h1>
        <p className={styles.subtitle}>
          Signed in as {user.display_name} — {roleLabel(user.role)}.
        </p>
      </header>

      {/*
        The permanent readout, not the banner's warning. `PasswordNotice`
        is deliberately silent until the date is close, which
        is right for something across every screen and wrong here: somebody on
        this page is asking about their account, and when their password expires
        is one of the two things it exists to tell them. This says it always,
        for every role, and carries the same amber and red as the date nears -
        so the page never shows two versions of one fact.
      */}
      <PasswordExpiry status={user.password} />

      <ChangePasswordCard />
    </div>
  );
}

function roleLabel(role: 'ADMIN' | 'TEAM' | 'VOLUNTEER'): string {
  if (role === 'ADMIN') return 'Administrator';
  if (role === 'TEAM') return 'Parinaam team';
  return 'Volunteer';
}

/**
 * The form, and the only place a password is chosen.
 *
 * Three fields rather than two. The confirmation is not ceremony: this is the
 * one form in the application where a typo cannot be discovered by looking at
 * what you typed, and where the cost of one is being locked out of the account
 * you were trying to secure.
 */
export function ChangePasswordCard({ forced = false }: { forced?: boolean }) {
  const { user, changePassword } = useAuth();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [pending, setPending] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setDone(false);

    /*
     * The policy first, then the match.
     *
     * Both fields are checked against the policy rather than only the first.
     * They must be identical to get past the next check, so testing the
     * second is strictly redundant — and it is here anyway, because "these
     * two boxes hold the same string" is a property of the form, while "this
     * password is acceptable" is a property of each value, and a later
     * refactor that relaxes the first should not silently relax the second.
     *
     * Order matters to the person typing: told "they do not match" first,
     * they retype the confirmation and are then told the password was never
     * acceptable to begin with.
     */
    if (!isPasswordValid(next) || !isPasswordValid(confirm)) {
      const problems = passwordProblems(isPasswordValid(next) ? confirm : next);
      setError(`That password needs: ${problems.join(', ').toLowerCase()}.`);
      return;
    }

    // No answer the server could give: it never sees the confirmation field.
    if (next !== confirm) {
      setError('The two new passwords do not match.');
      return;
    }

    setPending(true);
    try {
      await changePassword(current, next);
      setCurrent('');
      setNext('');
      setConfirm('');
      setDone(true);
    } catch (caught) {
      setError(
        caught instanceof ApiRequestError
          ? caught.message
          : 'Could not reach the system. Please try again.'
      );
    } finally {
      setPending(false);
    }
  };

  return (
    <Card title={forced ? 'Choose a new password' : 'Change your password'}>
      <form onSubmit={submit} noValidate className={styles.form}>
        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}
        {done && (
          <p className={styles.done} role="status">
            Password changed; other devices signed out.
          </p>
        )}

        <PasswordInput
          label="Current password"
          required
          autoComplete="current-password"
          value={current}
          onChange={(event) => setCurrent(event.target.value)}
        />
        <PasswordInput
          label="New password"
          required
          autoComplete="new-password"
          value={next}
          onChange={(event) => setNext(event.target.value)}
        />
        {/*
          Between the two fields, not under both. It belongs to the box above
          it — placed after the confirmation it would read as a comment on
          whichever field was touched last.
        */}
        <PasswordRules value={next} />
        <PasswordInput
          label="Confirm new password"
          required
          autoComplete="new-password"
          // Live, and only once there is something to compare. Telling
          // somebody the passwords do not match after one character is true
          // and useless.
          error={
            confirm.length > 0 && confirm !== next ? 'This does not match the new password.' : undefined
          }
          value={confirm}
          onChange={(event) => setConfirm(event.target.value)}
        />

        <div className={styles.actions}>
          {/*
            Disabled until every rule is green and the two match. The form
            still validates on submit — a disabled button is a convenience,
            not a check — but there is no reason to let somebody press it to
            be told what the checklist above is already showing them.
          */}
          <Button
            type="submit"
            variant="primary"
            disabled={
              pending || current.length === 0 || !isPasswordValid(next) || next !== confirm
            }
          >
            {pending ? 'Changing…' : 'Change password'}
          </Button>
        </div>

        {user?.password?.expires_at && !forced && (
          <p className={styles.footnote}>
            Passwords for volunteer and team accounts must be changed every 120 days.
          </p>
        )}
      </form>
    </Card>
  );
}
